/**
 * Tabela de rotas da Fórmula MED — porte tipado de `app/routes.js`.
 *
 * `app/routes.js` continua sendo a declaração que o protótipo e a documentação
 * citam; aqui ela vira dado tipado, com rótulo e ícone para a navegação. Os dois
 * não podem divergir em silêncio: `tests/unit/rotas.test.ts` lê `app/routes.js`
 * e falha se caminho, surface, guarda ou subrota saírem de sincronia.
 *
 * Uma aplicação, três surfaces:
 *   auth      fora de sessão
 *   clinic    produto usado pela clínica, escopado por tenant
 *   platform  painel da VecchIA sobre todas as clínicas
 *
 * A surface **não vem da URL**: vem do perfil na sessão (ver `guardas.ts`).
 */

import type { Surface } from '@/session/sessao';

/**
 * Guardas de rota:
 *   session   exige sessão válida
 *   tenant    exige clínica ativa no contexto
 *   platform  exige perfil de plataforma
 *   perm:X    exige a permissão nomeada
 */
export type Guarda = 'session' | 'tenant' | 'platform' | `perm:${string}`;

export interface Subrota {
    path: string;
    label: string;
}

export interface Rota {
    /** Id do módulo. É por ele que a navegação lateral casa rota e item. */
    id: string;
    path: string;
    surface: Surface;
    /** Nome da tela, como declarado em `app/routes.js`. */
    screen: string;
    label: string;
    icon: string;
    guards: Guarda[];
    children: Subrota[];
    /**
     * Tela do UI kit que define o visual.
     *
     * Ausente só em módulo que **não existe no protótipo** — hoje, Compras.
     * Deixar em branco seria dizer que existe e está vazio.
     */
    proto?: string;
    banner?: 'impersonation';
    readOnly?: boolean;
    /**
     * Módulo sob o qual esta rota aparece na barra lateral.
     *
     * `app/routes.js` declara `/app/atendimentos/fichas` como rota IRMÃ de
     * `/app/atendimentos`, não como subrota — e o menu, que só lista `children`,
     * deixava a tela sem caminho por clique. Isto é fato de navegação, não de
     * roteamento: o contrato de rotas continua igual ao do protótipo.
     */
    navPai?: string;
}

export const SURFACES: Record<Surface, { basePath: string; shell: string }> = {
    auth: { basePath: '/', shell: 'AuthShell' },
    clinic: { basePath: '/app', shell: 'ClinicShell' },
    platform: { basePath: '/admin', shell: 'PlatformShell' },
};

export const rotas: Rota[] = [
    /* ---------- auth ---------- */
    { id: 'entrar', path: '/entrar', surface: 'auth', screen: 'SignIn', label: 'Entrar', icon: 'log-in', guards: [], children: [], proto: 'ui_kits/vecchia_app/AuthScreen.jsx' },
    { id: 'criar-conta', path: '/criar-conta', surface: 'auth', screen: 'SignUp', label: 'Criar conta', icon: 'user-plus', guards: [], children: [], proto: 'ui_kits/vecchia_app/AuthScreen.jsx' },
    { id: 'recuperar-senha', path: '/recuperar-senha', surface: 'auth', screen: 'PasswordReset', label: 'Recuperar senha', icon: 'key-round', guards: [], children: [], proto: 'ui_kits/vecchia_app/AuthScreen.jsx' },

    /* ---------- clínica ---------- */
    {
        id: 'inicio', path: '/app', surface: 'clinic', screen: 'Inicio', label: 'Início', icon: 'house',
        guards: ['session', 'tenant'], children: [], proto: 'ui_kits/vecchia_app/InicioScreen.jsx',
    },
    {
        id: 'agenda', path: '/app/agenda', surface: 'clinic', screen: 'Agenda', label: 'Agenda', icon: 'calendar-days',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/AgendaScreen.jsx',
        children: [
            { path: 'dia', label: 'Visão do dia' },
            { path: 'semana', label: 'Visão semanal' },
            { path: 'mes', label: 'Visão mensal' },
            { path: 'bloqueios', label: 'Bloqueios e folgas' },
            { path: 'salas', label: 'Salas e recursos' },
        ],
    },
    {
        id: 'comunicacao', path: '/app/comunicacao', surface: 'clinic', screen: 'Comunicacao', label: 'Comunicação', icon: 'message-circle',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/ComunicacaoScreen.jsx',
        children: [
            { path: 'abertos', label: 'Abertos' },
            { path: 'pendentes', label: 'Pendentes' },
            { path: 'resolvidos', label: 'Resolvidos' },
        ],
    },
    {
        id: 'contatos', path: '/app/contatos', surface: 'clinic', screen: 'Contatos', label: 'Contatos', icon: 'users',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/ContatosScreen.jsx',
        children: [
            { path: 'pacientes', label: 'Pacientes' },
            { path: 'leads', label: 'Leads' },
            { path: 'profissionais', label: 'Profissionais' },
            { path: 'fornecedores', label: 'Fornecedores' },
            { path: 'prestadores', label: 'Prestadores de serviço' },
            { path: 'importar', label: 'Importar contatos' },
        ],
    },
    {
        id: 'paciente', path: '/app/contatos/:contatoId', surface: 'clinic', screen: 'Paciente', label: 'Paciente', icon: 'user',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/PacienteScreen.jsx',
        children: [
            { path: 'informacoes', label: 'Informações' },
            { path: 'linha-do-tempo', label: 'Linha do tempo' },
            { path: 'carteira', label: 'Carteira' },
            { path: 'pacotes', label: 'Pacotes' },
            { path: 'financeiro', label: 'Financeiro' },
            { path: 'orcamentos', label: 'Orçamentos' },
            { path: 'prontuario', label: 'Prontuário' },
            { path: 'documentos', label: 'Documentos' },
            { path: 'formularios', label: 'Formulários' },
        ],
    },
    {
        id: 'atendimentos', path: '/app/atendimentos', surface: 'clinic', screen: 'Atendimentos', label: 'Atendimentos', icon: 'stethoscope',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/AtendimentosScreen.jsx',
        children: [
            { path: 'hoje', label: 'Hoje' },
            { path: 'em-aberto', label: 'Em aberto' },
            { path: 'todos', label: 'Todos' },
        ],
    },
    {
        id: 'ficha-builder', path: '/app/atendimentos/fichas', surface: 'clinic', screen: 'FichaBuilder', label: 'Fichas de atendimento', icon: 'clipboard-pen',
        guards: ['session', 'tenant', 'perm:fichas.editar'], children: [], navPai: 'atendimentos',
        proto: 'ui_kits/vecchia_app/FichaBuilderScreen.jsx',
    },
    {
        id: 'atendimento-editor', path: '/app/atendimentos/editor/:atendimentoId', surface: 'clinic',
        screen: 'AtendimentoEditor', label: 'Consulta em curso', icon: 'clipboard-pen',
        guards: ['session', 'tenant'], children: [], proto: 'ui_kits/vecchia_app/AtendimentoEditorScreen.jsx',
    },
    {
        id: 'procedimentos', path: '/app/procedimentos', surface: 'clinic', screen: 'Procedimentos', label: 'Procedimentos', icon: 'clipboard-list',
        guards: ['session', 'tenant'], children: [], proto: 'ui_kits/vecchia_app/ProcedimentosScreen.jsx',
    },
    {
        id: 'planos-de-saude', path: '/app/planos', surface: 'clinic', screen: 'PlanosDeSaude', label: 'Planos de saúde', icon: 'shield-plus',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/PlanosScreen.jsx',
        children: [
            { path: 'dashboard', label: 'Dashboard' },
            { path: 'cadastrados', label: 'Planos cadastrados' },
        ],
    },
    {
        id: 'crm', path: '/app/crm', surface: 'clinic', screen: 'Crm', label: 'CRM', icon: 'target',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/CrmScreen.jsx',
        children: [
            { path: 'pipeline', label: 'Pipeline' },
            { path: 'lista', label: 'Lista de oportunidades' },
            { path: 'etapas', label: 'Etapas e pipelines' },
        ],
    },
    {
        id: 'vendas', path: '/app/vendas', surface: 'clinic', screen: 'Vendas', label: 'Vendas', icon: 'shopping-cart',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/VendasScreen.jsx',
        children: [
            { path: 'orcamentos', label: 'Orçamentos' },
            { path: 'aguardando', label: 'Aguardando resposta' },
            { path: 'aprovados', label: 'Aprovados' },
            { path: 'pacotes', label: 'Pacotes ativos' },
        ],
    },
    {
        id: 'financeiro', path: '/app/financeiro', surface: 'clinic', screen: 'Financeiro', label: 'Financeiro', icon: 'circle-dollar-sign',
        guards: ['session', 'tenant', 'perm:financeiro.ver'], proto: 'ui_kits/vecchia_app/FinanceiroScreen.jsx',
        children: [
            { path: 'visao', label: 'Visão geral' },
            { path: 'receber', label: 'Contas a receber' },
            { path: 'pagar', label: 'Contas a pagar' },
            { path: 'extrato', label: 'Extrato de movimentações' },
            { path: 'competencia', label: 'Relatório de competência' },
            { path: 'fluxo-diario', label: 'Fluxo de caixa diário' },
            { path: 'fluxo-mensal', label: 'Fluxo de caixa mensal' },
            { path: 'categorias', label: 'Relatório de categorias' },
            { path: 'contas', label: 'Contas financeiras' },
            { path: 'categorias-contas', label: 'Categorias de contas' },
            { path: 'metodos', label: 'Métodos de pagamento' },
            { path: 'maquininha', label: 'Gateways de pagamento' },
            /* NFS-e (0176): nasceu no porte, não havia no protótipo. */
            { path: 'notas', label: 'Notas fiscais' },
        ],
    },
    {
        id: 'estoque', path: '/app/estoque', surface: 'clinic', screen: 'Estoque', label: 'Estoque', icon: 'package',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/EstoqueScreen.jsx',
        children: [
            { path: 'produtos', label: 'Produtos' },
            /* A tela do protótipo tinha a aba e a declaração não tinha a rota:
               cadastro sem endereço não recarrega, não abre em outra guia e não
               entra em link. Mesmo caso do editor de atendimento. */
            { path: 'categorias', label: 'Categorias de produto' },
            { path: 'baixo', label: 'Abaixo do mínimo' },
            { path: 'reservas', label: 'Reservas' },
            { path: 'fornecedores', label: 'Fornecedores' },
            { path: 'marcas', label: 'Marcas' },
            { path: 'movimentacoes', label: 'Movimentações' },
        ],
    },
    {
        /* Único módulo sem `proto`: não existia no protótipo. Nasceu no porte
           para fechar o ciclo do estoque — ver `docs/ROUTE_MAP.md`. */
        id: 'compras', path: '/app/compras', surface: 'clinic', screen: 'Compras', label: 'Compras', icon: 'truck',
        guards: ['session', 'tenant'],
        children: [
            { path: 'sugestao', label: 'Sugestão de compra' },
            { path: 'abertos', label: 'Pedidos em aberto' },
            { path: 'recebidos', label: 'Recebidos' },
            { path: 'todos', label: 'Todos os pedidos' },
        ],
    },
    {
        /* Sem `proto`: a documentação viva da API não existia no protótipo. O
           endpoint já existe — o que faltava era o endereço dele escrito. */
        id: 'api', path: '/app/api', surface: 'clinic', screen: 'ApiDaClinica', label: 'API e integrações', icon: 'code',
        guards: ['session', 'tenant'], children: [],
    },
    {
        id: 'documentos', path: '/app/documentos', surface: 'clinic', screen: 'Documentos', label: 'Documentos', icon: 'file-text',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/DocumentosScreen.jsx',
        children: [
            { path: 'todos', label: 'Visão geral' },
            { path: 'assinaturas', label: 'Documentos e assinaturas' },
            { path: 'modelos', label: 'Modelos de documentos' },
        ],
    },
    {
        id: 'marketing', path: '/app/marketing', surface: 'clinic', screen: 'Marketing', label: 'Marketing', icon: 'megaphone',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/MarketingScreen.jsx',
        children: [
            { path: 'campanhas', label: 'Campanhas' },
            { path: 'ativas', label: 'Ativas' },
            { path: 'publicos', label: 'Públicos' },
            { path: 'criativos', label: 'Criativos' },
            /* Meta Ads (0162): a conta de anúncios da clínica e o que ela trouxe. */
            { path: 'anuncios', label: 'Anúncios' },
        ],
    },
    {
        id: 'automacoes', path: '/app/automacoes', surface: 'clinic', screen: 'Automacoes', label: 'Automações', icon: 'workflow',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/AutomacoesScreen.jsx',
        children: [
            { path: 'ativas', label: 'Ativas' },
            { path: 'pausadas', label: 'Pausadas' },
            { path: 'todas', label: 'Todas as automações' },
        ],
    },
    {
        /* Sem `proto`: no protótipo a IA era um drawer que respondia texto.
           Aqui ela é configurável, tem ferramentas e roda fluxo. */
        id: 'agentes', path: '/app/agentes', surface: 'clinic', screen: 'AgentesEIa', label: 'Agentes e IA', icon: 'bot',
        guards: ['session', 'tenant'],
        children: [
            { path: 'atendimento', label: 'Agentes de atendimento' },
            { path: 'fluxos', label: 'Fluxos' },
        ],
    },
    {
        /* ---- trilha B4: brain ----
           Sem `proto` e sem permissão de módulo: toda pessoa da clínica conversa
           com o Brain, e as conversas são privadas de quem as escreveu. O que ele
           pode FAZER é decidido pela borda, ferramenta por ferramenta, com as
           permissões de quem confirma a proposta. */
        id: 'brain', path: '/app/brain', surface: 'clinic', screen: 'Brain', label: 'VecchIA', icon: 'brain',
        guards: ['session', 'tenant'],
        children: [],
    },
    {
        id: 'relatorios', path: '/app/relatorios', surface: 'clinic', screen: 'Relatorios', label: 'Relatórios', icon: 'chart-no-axes-column',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/RelatoriosScreen.jsx',
        children: [
            { path: 'operacional', label: 'Operacional' },
            { path: 'financeiro', label: 'Financeiro' },
            { path: 'clinico', label: 'Clínico' },
            { path: 'comercial', label: 'Comercial' },
        ],
    },
    {
        id: 'configuracoes', path: '/app/configuracoes', surface: 'clinic', screen: 'Configuracoes', label: 'Configurações', icon: 'settings',
        guards: ['session', 'tenant'], proto: 'ui_kits/vecchia_app/ConfiguracoesScreen.jsx',
        children: [
            { path: 'clinica', label: 'Dados da clínica' },
            { path: 'preferencias', label: 'Preferências do sistema' },
            { path: 'equipe', label: 'Equipe' },
            { path: 'agenda', label: 'Agenda' },
            { path: 'notificacoes', label: 'Notificações' },
            { path: 'atendimento-crm', label: 'Atendimentos & CRM' },
            { path: 'integracoes', label: 'Integrações' },
            { path: 'assistente', label: 'Assistente' },
            { path: 'assinatura', label: 'Gerenciar assinatura' },
            { path: 'categorias', label: 'Categorias de procedimentos' },
            { path: 'pacotes', label: 'Pacotes' },
            { path: 'modelos', label: 'Modelos de atestados e prescrições' },
            { path: 'etiquetas', label: 'Etiquetas' },
        ],
    },

    /* ---------- plataforma (super admin) ---------- */
    {
        id: 'visao', path: '/admin', surface: 'platform', screen: 'Visao', label: 'Visão geral', icon: 'layout-dashboard',
        guards: ['session', 'platform'], children: [], proto: 'ui_kits/vecchia_admin/AdminNegocio.jsx',
    },
    {
        id: 'metricas', path: '/admin/metricas', surface: 'platform', screen: 'Metricas', label: 'Métricas', icon: 'trending-up',
        guards: ['session', 'platform'], proto: 'ui_kits/vecchia_admin/AdminNegocio.jsx',
        children: [
            { path: 'crescimento', label: 'Crescimento' },
            { path: 'churn', label: 'Churn e retenção' },
            { path: 'ltv', label: 'LTV e unit economics' },
            { path: 'ativacao', label: 'Ativação' },
        ],
    },
    {
        id: 'clinicas', path: '/admin/clinicas', surface: 'platform', screen: 'Clinicas', label: 'Clínicas', icon: 'building-2',
        guards: ['session', 'platform'], proto: 'ui_kits/vecchia_admin/AdminClinicas.jsx',
        children: [
            { path: 'todas', label: 'Todas as clínicas' },
            { path: 'trial', label: 'Em trial' },
            { path: 'risco', label: 'Em risco' },
            { path: 'inadimplentes', label: 'Inadimplentes' },
            { path: 'canceladas', label: 'Canceladas' },
        ],
    },
    {
        id: 'clinica', path: '/admin/clinicas/:clinicaId', surface: 'platform', screen: 'Clinica', label: 'Ficha da clínica', icon: 'building',
        guards: ['session', 'platform'], proto: 'ui_kits/vecchia_admin/AdminClinicas.jsx',
        children: [
            { path: 'resumo', label: 'Resumo' },
            { path: 'assinatura', label: 'Assinatura' },
            { path: 'unidades', label: 'Unidades' },
            { path: 'usuarios', label: 'Usuários' },
            { path: 'faturas', label: 'Faturas' },
            { path: 'ia', label: 'Consumo de IA' },
            { path: 'historico', label: 'Histórico' },
        ],
    },
    {
        id: 'onboarding', path: '/admin/onboarding', surface: 'platform', screen: 'Onboarding', label: 'Onboarding', icon: 'rocket',
        guards: ['session', 'platform'], children: [], proto: 'ui_kits/vecchia_admin/AdminSuporte.jsx',
    },
    {
        id: 'suporte', path: '/admin/suporte', surface: 'platform', screen: 'Suporte', label: 'Suporte', icon: 'headset',
        guards: ['session', 'platform'], children: [], proto: 'ui_kits/vecchia_admin/AdminSuporte.jsx',
    },
    {
        id: 'planos', path: '/admin/planos', surface: 'platform', screen: 'Planos', label: 'Planos e assinaturas', icon: 'layers',
        guards: ['session', 'platform', 'perm:planos.editar'], children: [], proto: 'ui_kits/vecchia_admin/AdminReceita.jsx',
    },
    {
        id: 'faturamento', path: '/admin/faturamento', surface: 'platform', screen: 'Faturamento', label: 'Faturamento', icon: 'circle-dollar-sign',
        guards: ['session', 'platform', 'perm:financeiro.operar'], proto: 'ui_kits/vecchia_admin/AdminReceita.jsx',
        children: [
            { path: 'resumo', label: 'Resumo' },
            { path: 'faturas', label: 'Faturas' },
            { path: 'inadimplencia', label: 'Inadimplência' },
            { path: 'notas', label: 'Notas fiscais' },
        ],
    },
    {
        id: 'ia', path: '/admin/ia', surface: 'platform', screen: 'ConsumoIa', label: 'Consumo de IA', icon: 'sparkles',
        guards: ['session', 'platform'], children: [], proto: 'ui_kits/vecchia_admin/AdminReceita.jsx',
    },
    {
        /* Sem `proto`: no protótipo a IA não tinha de onde sair. */
        id: 'integracoes', path: '/admin/integracoes', surface: 'platform', screen: 'IntegracoesDaPlataforma',
        label: 'Integrações e IA', icon: 'plug',
        guards: ['session', 'platform', 'perm:integracoes.editar'], children: [],
    },
    {
        id: 'usuarios', path: '/admin/usuarios', surface: 'platform', screen: 'Usuarios', label: 'Usuários e acessos', icon: 'users',
        guards: ['session', 'platform', 'perm:usuarios.gerenciar'], children: [], proto: 'ui_kits/vecchia_admin/AdminPlataforma.jsx',
    },
    {
        id: 'flags', path: '/admin/flags', surface: 'platform', screen: 'Flags', label: 'Feature flags', icon: 'flag',
        guards: ['session', 'platform', 'perm:flags.editar'], children: [], proto: 'ui_kits/vecchia_admin/AdminPlataforma.jsx',
    },
    {
        id: 'saude', path: '/admin/saude', surface: 'platform', screen: 'Saude', label: 'Saúde do sistema', icon: 'activity',
        guards: ['session', 'platform'], children: [], proto: 'ui_kits/vecchia_admin/AdminPlataforma.jsx',
    },
    {
        id: 'auditoria', path: '/admin/auditoria', surface: 'platform', screen: 'Auditoria', label: 'Log de auditoria', icon: 'scroll-text',
        guards: ['session', 'platform'], children: [], proto: 'ui_kits/vecchia_admin/AdminPlataforma.jsx',
    },

    /* ---------- impersonação: entra no surface da clínica com faixa de aviso ---------- */
    {
        id: 'impersonar', path: '/admin/impersonar/:clinicaId/:usuarioId', surface: 'clinic', screen: 'Inicio',
        label: 'Impersonação', icon: 'eye',
        guards: ['session', 'platform', 'perm:impersonar'], banner: 'impersonation', readOnly: true,
        children: [], proto: 'ui_kits/vecchia_admin/AdminSuporte.jsx',
    },
];

export interface SecaoDeNavegacao {
    section: string;
    items: string[];
}

export const clinicNav: SecaoDeNavegacao[] = [
    { section: 'Operação', items: ['inicio', 'comunicacao', 'agenda', 'contatos', 'atendimentos', 'procedimentos', 'planos-de-saude'] },
    { section: 'Comercial', items: ['crm', 'vendas', 'financeiro', 'estoque', 'compras'] },
    { section: 'Relacionamento', items: ['documentos', 'marketing', 'automacoes', 'agentes', 'brain', 'relatorios'] },
    { section: 'Configuração', items: ['configuracoes', 'api'] },
];

export const platformNav: SecaoDeNavegacao[] = [
    { section: 'Negócio', items: ['visao', 'metricas'] },
    { section: 'Clientes', items: ['clinicas', 'onboarding', 'suporte'] },
    { section: 'Receita', items: ['planos', 'faturamento', 'ia'] },
    { section: 'Plataforma', items: ['integracoes', 'usuarios', 'flags', 'saude', 'auditoria'] },
];

export function rotaPorId(id: string): Rota | undefined {
    return rotas.find((r) => r.id === id);
}

export function rotasDaSurface(surface: Surface): Rota[] {
    return rotas.filter((r) => r.surface === surface);
}
